import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiHeart, FiAlertCircle, FiShoppingBag } from "react-icons/fi";
import { Link } from "react-router-dom";
import api from "../services/api";
import ProductCard from "../components/ProductCard";
import Loader from "../components/Loader";

const FavoritesPage = () => {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchFavorites = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get("/favorites/");
        setFavorites(response.data.results || response.data);
      } catch (err) {
        setError("Failed to synchronize your saved assets.");
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  if (loading) return <Loader />;

  if (error)
    return (
      <div className="max-w-4xl mx-auto px-4 my-20 animate-fade-in">
        <div className="bg-rose-50 border border-rose-100 p-12 rounded-[48px] text-center space-y-6 shadow-2xl shadow-rose-500/5">
          <div className="w-20 h-20 bg-rose-500 text-white rounded-3xl flex items-center justify-center mx-auto shadow-xl">
             <FiAlertCircle size={40} />
          </div>
          <h2 className="text-2xl font-black text-theme-text uppercase tracking-widest">{error}</h2>
          <button onClick={() => window.location.reload()} className="px-10 h-14 bg-theme-text text-white font-black rounded-full text-xs uppercase tracking-widest hover:bg-black transition-all">Retry Sync</button>
        </div>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 md:py-24 min-h-[60vh]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 pb-8 border-b border-component-border">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
             <div className="w-8 h-1 bg-chafadi-primary rounded-full" />
             <span className="text-[10px] font-black text-chafadi-primary uppercase tracking-[0.4em]">Personal Archive</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-theme-text tracking-tight font-display uppercase">{t("favoritesPage.title")}</h1>
          <div className="flex items-center gap-3">
             <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
             <p className="text-[10px] font-black text-theme-muted uppercase tracking-widest">{favorites.length} Assets Logged</p>
          </div>
        </div>
      </div>

      {favorites.length > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-10 animate-fade-in">
          {favorites.map((fav) => (
            <div key={fav.id} className="transition-all hover:-translate-y-2 duration-500">
               <ProductCard product={fav.product} />
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-[60px] border border-component-border p-24 text-center shadow-2xl shadow-gray-200/20 max-w-4xl mx-auto animate-fade-in">
          <div className="w-32 h-32 bg-theme-bg rounded-full flex items-center justify-center mx-auto mb-10 border border-component-border shadow-inner">
            <FiHeart size={64} className="text-theme-muted/20" />
          </div>
          <h4 className="text-3xl font-black text-theme-text mb-4 uppercase tracking-tighter font-display">
            {t("favoritesPage.noFavorites")}
          </h4>
          <p className="text-sm font-medium text-theme-muted uppercase tracking-[0.2em] leading-relaxed max-w-sm mx-auto mb-12">
            {t("favoritesPage.subtext")}
          </p>
          <Link to="/search/" className="inline-flex h-16 px-12 bg-theme-text text-white rounded-full font-black text-[10px] uppercase tracking-[0.4em] items-center justify-center hover:bg-black transition-all shadow-xl shadow-gray-900/10">
            <FiShoppingBag className="mr-3" /> Initialize Search Protocol
          </Link>
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;
